/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { IProps } from './email-daily-sending-limit-container';

import toaster, { Theme } from '../../../../../../shared/toaster';
import Button from '../../../../../../shared/design-system/components/atoms/button';

import FeatureCodes from '../../../../../../shared/enums/feature-codes';
import { redirectWithoutRefresh } from '../../../../../../shared/utils';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import { getFeatureCodeValue } from '../../../../../../shared/utils/feature-code';

import {
  EmailAccountMethod,
  EmailAccountType,
} from '../../../../enums/email-account';
import { getIsRequestPending } from '../update-email-account/helpers';
import { RouteParams, UpdateEmailTabKeys } from '../update-email-account/types';

import AddEmailAccountModal from '../add-email-account-modal';
import AddBulkImportModal from '../add-email-bulk-import-modal';
import NoSeatAvailableForEmailModal from '../no-seat-available-for-email-modal';

import DailySendingLimitInput from './sub-components/daily-sending-limit-input';
import ZeroLimitErrorNudge from './sub-components/zero-limit-error';
import UnifiedHardLimitBanner from './sub-components/unified-hard-limit-banner';
import LowerLimitErrorNudge from './sub-components/lower-limit-error';
import HardLimitErrorNudge from './sub-components/hard-limit-error';

import { resetEmailDailySendingLimitState } from './email-daily-sending-limit-slice';
import {
  zeroLimitChecker,
  hardLimitChecker,
  getLimitsForEmailAccount,
  lowerLimitChecker,
  saveButtonDisabledChecker,
  shouldAllowLimitUpdateChecker,
  getSettingsLimitForEmailAccount,
  executeBasedOnSubscriptionPlan,
  getEmailServiceProvider,
  getInboxAnalyzerLimitsForEmailAccount,
} from './helpers';
import { resetToDefaultSendingLimits } from '../../../../constants/esp-sending-limits';
import InboxPlacementDailySendingLimitInput from './sub-components/inbox-placement-daily-sending-limit-input';
import WarmUpDailySendingLimitRow from './sub-components/warm-up-daily-sending-limit-row';
import { EmailAccountSettingCode } from '../../../../enums/email-account-settings';
import { OverlayTooltip } from '../../../../../../shared/design-system/components/overlay';
import { Images } from '../../../../../../shared/app-constants';
import { ESPs } from './types';

const EmailDailySendingLimit: React.FC<IProps> = ({
  subscriptionPlan,
  sendConnectEmailAccountRequest,
  connectEmailAccountRequestStatus,
  sendGetEspLimitRequest,
  espLimits,
  inboxEspLimits,
  emailAccount,
  sendUpdateSendingLimitsRequest,
  sendGetEmailAccountSettingsRequest,
  emailAccountSettings,
  updateSendingLimitsStatus,
  resetUpdateSendingLimitsRequestState,
  firstName,
  sendGetEmailAccountSetupScoreRequest,
  isBulkUpdatingEmailAccounts,
  setSendingLimit: setSendingLimitBulk,
  setInboxSendingLimit: setInboxSendingLimitBulk,
  setSendingLimitError,
  setSendingInboxLimitError,
  featureUsages,
}) => {
  const { t } = useTranslation();
  const params = useParams<RouteParams>();
  const { hashId } = params;
  const emailAccountIdParam = emailAccount?.id;

  // Preserve last known warmup limit so totalDailyLimit is correct when warmupDetails is null on first render or refetch
  const lastWarmupLimitRef = useRef<number>(0);

  const { hardLimit } = getLimitsForEmailAccount({
    emailAccount,
    espLimits,
  });
  const { inboxHardLimit } = getInboxAnalyzerLimitsForEmailAccount({
    emailAccount,
    inboxEspLimits,
  });

  const maxLimit = useMemo(() => {
    if (emailAccount?.domainMailboxId !== null) {
      if (
        emailAccount?.type === EmailAccountType.Gmail ||
        emailAccount?.type === EmailAccountType.Microsoft
      ) {
        return 50;
      }
      if (emailAccount?.type === EmailAccountType.SmtpImap) {
        return 60;
      }
    }

    return parseInt(
      getFeatureCodeValue(
        featureUsages,
        FeatureCodes.EmailAccountSendingMaxDailySending,
      )?.remainingQuota ||
        (isBulkUpdatingEmailAccounts ? '4000' : String(hardLimit)),
      10,
    );
  }, [emailAccount, featureUsages, isBulkUpdatingEmailAccounts, hardLimit]);

  const sequenceEmailAccountSoftLimit = useMemo(() => {
    const foundEsp = ((espLimits as unknown) as ESPs[])?.find(
      (esp) => emailAccount?.emailServiceProvider === esp?.esp,
    );

    const softLimit =
      foundEsp && foundEsp.softLimit != null
        ? foundEsp.softLimit
        : resetToDefaultSendingLimits.outreachDefault;
    return parseInt(String(softLimit), 10);
  }, [espLimits, emailAccount, resetToDefaultSendingLimits]);

  const inboxEmailAccountSoftLimit = useMemo(() => {
    const foundEsp = ((inboxEspLimits as unknown) as ESPs[])?.find(
      (esp) => emailAccount?.emailServiceProvider === esp.esp,
    );
    const softLimit =
      foundEsp && foundEsp.softLimit != null
        ? foundEsp.softLimit
        : resetToDefaultSendingLimits.inboxPlacementDefault;
    return parseInt(String(softLimit), 10);
  }, [inboxEspLimits, emailAccount, resetToDefaultSendingLimits]);

  const inboxMaxLimit = useMemo(
    () =>
      parseInt(
        getFeatureCodeValue(
          featureUsages,
          FeatureCodes.InboxAnalyzerEmailAccountSendingDailyLimit,
        )?.remainingQuota ||
          (isBulkUpdatingEmailAccounts ? '70' : String(inboxHardLimit)),
        10,
      ),
    [emailAccount, featureUsages, isBulkUpdatingEmailAccounts, inboxHardLimit],
  );

  const {
    maxSendingLimit,
    rampUpStatus,
    rampUpLimit,
  } = getSettingsLimitForEmailAccount(emailAccountSettings);

  const inboxPlacementFromSettings = useMemo(() => {
    if (emailAccountSettings && Array.isArray(emailAccountSettings)) {
      const setting = emailAccountSettings.find(
        (s) => s.code === EmailAccountSettingCode.InboxAnalyzerDailyLimit,
      );
      if (setting?.value != null) return Number(setting.value);
    }
    return null;
  }, [emailAccountSettings]);

  const [sendingLimit, setSendingLimit] = useState<number>(null);
  const [
    inboxPlacementSendingLimit,
    setInboxPlacementSendingLimit,
  ] = useState<number>(null);
  const [editingInboxPlacement, setEditingInboxPlacement] = useState(false);
  const [
    showAddEmailAccountModal,
    setShowAddEmailAccountModal,
  ] = useState<boolean>(false);

  const [zeroLimitError, setZeroLimitError] = useState<boolean>(false);
  const [hardLimitError, setHardLimitError] = useState<boolean>(false);
  const [
    inboxPlacementHardLimitError,
    setInboxPlacementHardLimitError,
  ] = useState<boolean>(false);
  const [
    inboxPlacementZeroLimitError,
    setInboxPlacementZeroLimitError,
  ] = useState<boolean>(false);
  const [lowerLimitError, setLowerLimitError] = useState<boolean>(false);
  const [
    showNoSeatAvailableForEmailModal,
    setShowNoSeatAvailableForEmailModal,
  ] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isInboxPlacementLimitDirty, setIsInboxPlacementLimitDirty] = useState(
    false,
  );
  const [showBulkModal, setShowBulkModal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [
    isInboxPlacementDisabled,
    setIsInboxPlacementDisabled,
  ] = useState<boolean>(false);

  const isUpdateSendingLimitsLoading = getIsRequestPending(
    updateSendingLimitsStatus,
  );

  const hideEmailAccountModal = () => setShowAddEmailAccountModal(false);

  const hideNoSeatAvailableForEmailModal = () => {
    setShowNoSeatAvailableForEmailModal(false);
  };

  const onConnectAnotherEmailAccount = () => {
    redirectWithoutRefresh('/email-accounts/create');
  };

  const addEmailAccountHandler = (
    method: EmailAccountMethod,
    emailAccountId?: number,
  ) => {
    executeBasedOnSubscriptionPlan(subscriptionPlan, () => {
      sendConnectEmailAccountRequest(method, emailAccountId);
    });
  };

  const changeSendingLimit = (value) => {
    if (value === '' && isBulkUpdatingEmailAccounts) {
      setSendingLimit(null);
      setZeroLimitError(false);
      setLowerLimitError(false);
      setHardLimitError(false);
      setSendingLimitError?.(false);
      setIsDirty(false);
      setSendingLimitBulk?.(null);
      setSendingLimitError?.(false);
      return;
    }

    setSendingLimit(value);

    if (zeroLimitChecker(value, isBulkUpdatingEmailAccounts, maxLimit)) {
      setZeroLimitError(true);
      setLowerLimitError(false);
      setHardLimitError(false);
      setSendingLimitError?.(true);
      return;
    }

    setZeroLimitError(false);

    if (lowerLimitChecker(value, rampUpStatus, rampUpLimit)) {
      setLowerLimitError(true);
      setZeroLimitError(false);
      setHardLimitError(false);
      setSendingLimitError?.(true);
      return;
    }

    setLowerLimitError(false);

    if (hardLimitChecker(value, hardLimit, maxLimit)) {
      setHardLimitError(true);
      setZeroLimitError(false);
      setLowerLimitError(false);
      setSendingLimitError?.(true);
      return;
    }

    setHardLimitError(false);

    if (!isDirty) {
      setIsDirty(true);
    }

    setSendingLimitBulk?.(value);
    setSendingLimitError?.(false);
  };

  const changeInboxPlacementSendingLimit = (value) => {
    if (!editingInboxPlacement) {
      setEditingInboxPlacement(true);
    }

    if (value === '' && isBulkUpdatingEmailAccounts) {
      setInboxPlacementSendingLimit(null);
      setInboxPlacementZeroLimitError(false);
      setLowerLimitError(false);
      setInboxPlacementHardLimitError(false);
      setSendingInboxLimitError?.(false);
      setIsInboxPlacementLimitDirty(false);
      setInboxSendingLimitBulk?.(null);
      setSendingInboxLimitError?.(false);
      return;
    }

    setInboxPlacementSendingLimit(value);

    if (zeroLimitChecker(value, isBulkUpdatingEmailAccounts, inboxMaxLimit)) {
      setInboxPlacementZeroLimitError(true);
      setLowerLimitError(false);
      setInboxPlacementHardLimitError(false);
      setSendingInboxLimitError?.(true);
      return;
    }

    setInboxPlacementZeroLimitError(false);

    if (lowerLimitChecker(value, rampUpStatus, rampUpLimit)) {
      setLowerLimitError(true);
      setInboxPlacementZeroLimitError(false);
      setInboxPlacementHardLimitError(false);
      setSendingInboxLimitError?.(true);
      return;
    }

    setLowerLimitError(false);

    if (hardLimitChecker(value, inboxHardLimit, inboxMaxLimit)) {
      setInboxPlacementHardLimitError(true);
      setInboxPlacementZeroLimitError(false);
      setLowerLimitError(false);
      setSendingInboxLimitError?.(true);
      return;
    }

    setInboxPlacementHardLimitError(false);

    if (!isInboxPlacementLimitDirty) {
      setIsInboxPlacementLimitDirty(true);
    }

    setInboxSendingLimitBulk?.(value);
    setSendingInboxLimitError?.(false);
  };

  const isSection1Dirty = isDirty || isInboxPlacementLimitDirty;
  const isSection1SaveDisabled =
    (isDirty && isDisabled) ||
    (isInboxPlacementLimitDirty && isInboxPlacementDisabled) ||
    isUpdateSendingLimitsLoading;

  const onSection1SaveHandler = () => {
    if (
      isDirty &&
      shouldAllowLimitUpdateChecker(
        zeroLimitError,
        hardLimitError,
        lowerLimitError,
        sendingLimit > maxLimit,
        isUpdateSendingLimitsLoading,
      )
    ) {
      return;
    }
    if (
      isInboxPlacementLimitDirty &&
      shouldAllowLimitUpdateChecker(
        inboxPlacementZeroLimitError,
        inboxPlacementHardLimitError,
        lowerLimitError,
        inboxPlacementSendingLimit > inboxMaxLimit,
        isUpdateSendingLimitsLoading,
      )
    ) {
      return;
    }
    const payload: {
      emailAccountId: number;
      maxSendingLimit?: number;
      inboxPlacementDailySendingLimit?: number;
    } = { emailAccountId: emailAccountIdParam };
    if (isDirty) payload.maxSendingLimit = Number(sendingLimit);
    if (isInboxPlacementLimitDirty)
      {payload.inboxPlacementDailySendingLimit = Number(
        inboxPlacementSendingLimit,
      );}
    if (
      payload.maxSendingLimit !== undefined ||
      payload.inboxPlacementDailySendingLimit !== undefined
    ) {
      sendUpdateSendingLimitsRequest(payload);
    }
  };

  const onResetToDefaultHandler = () => {
    setSendingLimit(sequenceEmailAccountSoftLimit);
    setInboxPlacementSendingLimit(inboxEmailAccountSoftLimit);
    setIsDirty(false);
    setIsInboxPlacementLimitDirty(false);
    setZeroLimitError(false);
    setHardLimitError(false);
    setLowerLimitError(false);
    setInboxPlacementZeroLimitError(false);
    setInboxPlacementHardLimitError(false);
    setSendingLimitBulk?.(sequenceEmailAccountSoftLimit);
    setInboxSendingLimitBulk?.(inboxEmailAccountSoftLimit);
    setSendingLimitError?.(false);
    setSendingInboxLimitError?.(false);
    sendUpdateSendingLimitsRequest({
      emailAccountId: emailAccountIdParam,
      maxSendingLimit: sequenceEmailAccountSoftLimit,
      inboxPlacementDailySendingLimit: inboxEmailAccountSoftLimit,
    });
  };

  // Use warmupDetails when set (from slice normalization), else derive from tiWarmupDetails so first render is correct
  const warmupFromDetails =
    emailAccount?.warmupDetails?.maximumSendingLimit != null
      ? Number(emailAccount.warmupDetails.maximumSendingLimit)
      : null;
  let warmupFromTi: number | null = null;
  if (
    warmupFromDetails == null &&
    Array.isArray(emailAccount?.tiWarmupDetails) &&
    emailAccount.tiWarmupDetails.length > 0
  ) {
    const entry = emailAccount.tiWarmupDetails[0];
    warmupFromTi = Number(entry.maximumSendingLimit);
  }
  const warmupLimit =
    warmupFromDetails ?? warmupFromTi ?? lastWarmupLimitRef.current;
  if (warmupFromDetails != null || warmupFromTi != null) {
    lastWarmupLimitRef.current = warmupLimit;
  }

  // Use settings-backed values when state is still null so first render shows correct total
  const effectiveSendingLimit =
    sendingLimit != null ? Number(sendingLimit) : maxSendingLimit ?? 0;
  const effectiveInboxLimit =
    inboxPlacementSendingLimit != null
      ? Number(inboxPlacementSendingLimit)
      : inboxPlacementFromSettings ?? 0;

  const totalDailyLimit =
    effectiveSendingLimit + effectiveInboxLimit + warmupLimit;

  const resetComponentState = () => {
    setSendingLimit(maxSendingLimit);
    setShowAddEmailAccountModal(false);
    setZeroLimitError(false);
    setHardLimitError(false);
    setLowerLimitError(false);
    setInboxPlacementSendingLimit(inboxPlacementSendingLimit);
    setInboxPlacementHardLimitError(false);
    setInboxPlacementZeroLimitError(false);
  };

  const showBulkImportModal = () => setShowBulkModal(true);

  const hideBulkImportModal = () => setShowBulkModal(false);

  const showConnectAndSaveSmtpImapAccountModel = () => {
    setShowAddEmailAccountModal(false);
  };

  const showBulkImportCSV = () => {
    hideEmailAccountModal();
    showBulkImportModal();
  };

  useEffect(() => {
    executeOnRequestStatus({
      status: updateSendingLimitsStatus,
      onSuccess: () => {
        toaster.success(t('messages.email_per_day_success_message'), {
          theme: Theme.New,
        });
        setIsDirty(false);
        setIsInboxPlacementLimitDirty(false);
        sendGetEmailAccountSettingsRequest(hashId);
        resetUpdateSendingLimitsRequestState();
        sendGetEmailAccountSetupScoreRequest({
          emailAccountId: emailAccountIdParam,
        });
      },
    });
  }, [updateSendingLimitsStatus]);

  const isConnectEmailAccountRequestPending =
    connectEmailAccountRequestStatus === RequestStatus.Pending;

  useEffect(() => {
    sendGetEspLimitRequest();
  }, []);

  useEffect(() => {
    if (!isBulkUpdatingEmailAccounts) {
      setSendingLimit(maxSendingLimit);
    }
  }, [maxSendingLimit]);

  useEffect(() => {
    if (emailAccountSettings && Array.isArray(emailAccountSettings)) {
      const inboxPlacementSetting = emailAccountSettings.find(
        (setting) =>
          setting.code === EmailAccountSettingCode.InboxAnalyzerDailyLimit,
      );

      if (inboxPlacementSetting) {
        setInboxPlacementSendingLimit(Number(inboxPlacementSetting.value));
      }
    }
  }, [emailAccountSettings]);

  // Cleaning state on unmounting component.
  useEffect(
    () => () => {
      resetEmailDailySendingLimitState();
    },
    [],
  );

  useEffect(
    () => () => {
      if (params.tab === UpdateEmailTabKeys.SENDING_SETTINGS) {
        resetComponentState();
      }
    },
    [params],
  );

  useEffect(() => {
    setIsDisabled(
      saveButtonDisabledChecker(
        zeroLimitError,
        hardLimitError,
        lowerLimitError,
        sendingLimit > maxLimit,
        isUpdateSendingLimitsLoading,
      ),
    );
  }, [
    zeroLimitError,
    hardLimitError,
    lowerLimitError,
    sendingLimit,
    isUpdateSendingLimitsLoading,
  ]);

  useEffect(() => {
    setIsInboxPlacementDisabled(
      saveButtonDisabledChecker(
        inboxPlacementZeroLimitError,
        inboxPlacementHardLimitError,
        lowerLimitError,
        inboxPlacementSendingLimit > inboxMaxLimit,
        isUpdateSendingLimitsLoading,
      ),
    );
  }, [
    inboxPlacementZeroLimitError,
    inboxPlacementHardLimitError,
    lowerLimitError,
    inboxPlacementSendingLimit,
    isUpdateSendingLimitsLoading,
  ]);

  const emailServiceProvider = getEmailServiceProvider(emailAccount);

  const showUnifiedHardLimitBanner =
    !isBulkUpdatingEmailAccounts && totalDailyLimit > 30;
  const unifiedBannerHardLimit = 30;

  const hasLoadedSendingLimits =
    emailAccountSettings &&
    Array.isArray(emailAccountSettings) &&
    emailAccountSettings.length > 0;
  const isDefaultSendingLimits =
    hasLoadedSendingLimits &&
    Number(effectiveSendingLimit) ===
      resetToDefaultSendingLimits.outreachDefault &&
    Number(effectiveInboxLimit) ===
      resetToDefaultSendingLimits.inboxPlacementDefault;

  return (
    <div className="email-account-daily-sending-limit">
      <DailySendingLimitInput
        t={t}
        sendingLimit={sendingLimit}
        changeSendingLimit={changeSendingLimit}
        zeroLimitError={zeroLimitError}
        isBulkUpdatingEmailAccounts={isBulkUpdatingEmailAccounts}
        maxLimit={maxLimit}
      />

      <ZeroLimitErrorNudge
        zeroLimitError={zeroLimitError}
        maxLimit={maxLimit}
        isBulkUpdatingEmailAccounts={isBulkUpdatingEmailAccounts}
        t={t}
      />

      <LowerLimitErrorNudge t={t} lowerLimitError={lowerLimitError} />

      {hardLimitError && (
        <HardLimitErrorNudge
          hardLimitError={
            !isBulkUpdatingEmailAccounts &&
            (hardLimitError || sendingLimit > 1000)
          }
          hardLimit={hardLimit}
          onConnectAnotherEmailAccount={onConnectAnotherEmailAccount}
          emailServiceProvider={emailServiceProvider}
        />
      )}

      <InboxPlacementDailySendingLimitInput
        t={t}
        sendingLimit={inboxPlacementSendingLimit}
        changeSendingLimit={changeInboxPlacementSendingLimit}
        zeroLimitError={inboxPlacementZeroLimitError}
        isBulkUpdatingEmailAccounts={isBulkUpdatingEmailAccounts}
        maxLimit={inboxMaxLimit}
      />
      {editingInboxPlacement && (
        <ZeroLimitErrorNudge
          zeroLimitError={inboxPlacementZeroLimitError}
          maxLimit={inboxMaxLimit}
          isBulkUpdatingEmailAccounts={isBulkUpdatingEmailAccounts}
          t={t}
        />
      )}

      {editingInboxPlacement && (
        <HardLimitErrorNudge
          hardLimitError={
            !isBulkUpdatingEmailAccounts &&
            inboxPlacementSendingLimit !== null &&
            (inboxPlacementHardLimitError || inboxPlacementSendingLimit > 70)
          }
          hardLimit={inboxHardLimit}
          onConnectAnotherEmailAccount={onConnectAnotherEmailAccount}
          emailServiceProvider={emailServiceProvider}
        />
      )}

      {!isBulkUpdatingEmailAccounts && (
        <WarmUpDailySendingLimitRow
          warmupDetails={emailAccount?.warmupDetails}
        />
      )}

      <UnifiedHardLimitBanner
        show={showUnifiedHardLimitBanner}
        hardLimit={unifiedBannerHardLimit}
        onConnectAnotherEmailAccount={onConnectAnotherEmailAccount}
        emailServiceProvider={emailServiceProvider}
      />

      {!isBulkUpdatingEmailAccounts && (
        <>
          <div className="sending-settings-section-divider" />
          <div className="sending-settings-section-footer">
            <div className="d-flex align-items-center gap-3">
              <Button
                variant={Button.Variant.Primary}
                type={Button.Type.Submit}
                onClick={onSection1SaveHandler}
                isLoading={isUpdateSendingLimitsLoading}
                className={`header-btn section-save-btn ${
                  !isSection1Dirty ? 'opacity-35' : ''
                } ${
                  isSection1SaveDisabled && isSection1Dirty ? 'disabled' : ''
                }`}
                disabled={!isSection1Dirty || isSection1SaveDisabled}
              >
                Save
              </Button>
              <button
                type="button"
                className="reset-to-default-btn"
                onClick={onResetToDefaultHandler}
                disabled={!hasLoadedSendingLimits || isDefaultSendingLimits}
              >
                Reset to default
              </button>
            </div>
            <div className="d-flex align-items-center gap-2 total-daily-limit-badge-wrap">
              <span className="total-daily-limit-label">
                Total daily sending limit:
              </span>
              <OverlayTooltip
                className="sending-settings-tooltip-dark"
                text="This is the total number of emails you can send each day, including outreach, warm-up, and Inbox placement test emails."
              >
                <span className="total-daily-limit-badge">
                  {totalDailyLimit} emails
                  <img
                    className="info-icon"
                    src={Images.InfoCircle}
                    alt="Info"
                    aria-hidden
                  />
                </span>
              </OverlayTooltip>
            </div>
          </div>
        </>
      )}

      <AddEmailAccountModal
        onCSVSelect={showBulkImportCSV}
        show={showAddEmailAccountModal}
        onSubmit={addEmailAccountHandler}
        onClose={hideEmailAccountModal}
        isRequestPending={isConnectEmailAccountRequestPending}
        t={t}
        onSMTPAccountSelect={showConnectAndSaveSmtpImapAccountModel}
      />

      <AddBulkImportModal show={showBulkModal} onCancel={hideBulkImportModal} />

      <NoSeatAvailableForEmailModal
        show={showNoSeatAvailableForEmailModal}
        onClose={hideNoSeatAvailableForEmailModal}
        firstName={firstName}
      />
    </div>
  );
};

export default EmailDailySendingLimit;
